import { FieldValue, increment } from 'firebase/firestore';
import { uniq } from 'lodash';
import uniqid from 'uniqid';
import {
  Cafe,
  Dish,
  DishVariation,
  LegalEntity,
  Order,
  OrderItem,
  OrderItemStatus,
  OrderLog,
  Product,
  SelfOrderPoint,
  SemiProduct,
  Store,
  StoreLog,
  Worker,
} from '../../apiTypes';
import { createBatch } from '../../utils/api';
import { concatComponents } from '../../utils/concatComponents';
import { getOrderItemCost } from '../../utils/getPriceByUnit';
import { Map } from '../../utils/map';

type Props = {
  cafeSlug: string;
  cafe: DbDocument<Cafe>;
  order: DbDocument<Order>;
  dish: DbDocument<Dish>;
  variation: DishVariation | null;
  worker: DbDocument<Worker>;
  legalEntities: DbDocument<LegalEntity>[];
  productsMap: Map<DbDocument<Product>>;
  semiProductsMap: Map<DbDocument<SemiProduct>>;
  selfOrderPointsMap: Map<DbDocument<SelfOrderPoint>>;
};

const getStatus = (cafe: DbDocument<Cafe>): OrderItemStatus => {
  if (cafe.orderStatusesSetting === 'off') {
    return 'complete';
  }
  return 'ordered';
};

export const addItemToOrder = async ({
  order,
  cafeSlug,
  dish,
  variation,
  worker,
  cafe,
  legalEntities,
  productsMap,
  semiProductsMap,
  selfOrderPointsMap,
}: Props) => {
  const canEdit =
    (worker?.credentials.create_and_edit_own_orders && order.workerId === worker.id) ||
    worker?.credentials.edit_orders_of_other_employees;

  if (!canEdit) {
    return;
  }

  const status = getStatus(cafe);

  const components =
    variation && variation.components.length > 0
      ? concatComponents(dish.components, variation.components)
      : dish.components;

  const selfOrderPoint = order.selfOrderPointId ? selfOrderPointsMap[order.selfOrderPointId] : null;

  const batch = createBatch();
  const storesToUpdate = selfOrderPoint?.storeId ? [selfOrderPoint.storeId] : uniq(components.map((c) => c.store));
  const id = uniqid();
  const addTime = Date.now();
  const item: OrderItem = {
    id,
    dishId: dish.id,
    name: dish.name,
    price: dish.price,
    variation,
    status,
    queue: '1',
    addTime,
    byWeight: dish.byWeight || false,
    defaultWeight: dish.defaultWeight || '',
    weight: dish.defaultWeight || '',
    unit: dish.unit || 'g',
    legalEntityId: dish.taxInfo?.legalEntityId || legalEntities[0]?.id || null,
    cost: getOrderItemCost({ components, productsMap, semiProductsMap }),
  };

  const orderLog: OrderLog = {
    time: Date.now(),
    workerId: worker.id,
    workerName: worker.name,
    type: 'addToOrder',
    dishName: dish.name,
    state: {
      workerId: order.workerId,
      workerName: order.workerName,
      tableId: order.tableId,
      tableName: order.tableName,
      clientId: order.clientId || '',
      clientName: order.clientName || '',
      discount: order.discount || 0,
      items: { ...order.items, id: item },
      paid: order.paid,
      status: order.status || null,
      delivery: order.delivery || null,
    },
  };

  batch.update<Order>(`cafes/${cafeSlug}/orders/${order?.id}`, {
    [`items.${id}`]: item as unknown as FieldValue,
  });

  batch.set<OrderLog>(`cafes/${cafeSlug}/orders/${order?.id}/logs/${uniqid()}`, orderLog);

  storesToUpdate.forEach((s) => {
    if (s) {
      const diff: Record<string, number> = {};
      const updates: Record<`residues.${string}`, FieldValue> = {};

      components.forEach((c) => {
        if (c.store === s || selfOrderPoint?.storeId) {
          updates[`residues.${c.id}`] = increment(-c.qnt);
          diff[c.id] = -c.qnt;
        }
      });

      batch.update<Store>(`cafes/${cafeSlug}/stores/${s}`, updates);

      batch.set<StoreLog>(`cafes/${cafeSlug}/storeLogs/${uniqid()}`, {
        time: addTime,
        workerId: worker.id,
        workerName: worker.name,
        storeId: s,
        type: 'addToOrder',
        diff,
        info: {
          type: 'addToOrder',
          number: order.number,
          id: order.id,
          dishName: dish.name,
          dishId: dish.id,
        },
      });
    }
  });

  await batch.commit();
};
